import { Typography } from '@mui/material';
import { clsx } from 'clsx';
import React, { memo, useMemo } from 'react';

import {
  ScatterChart,
  ScatterChartProps,
} from '~/pages/Company/components/Inference/components/ScatterChart';
import { GetCompanyInferenceResponse } from '~/services/rtkQuery/company/types';

import styles from './styles.module.scss';

const Y_AXIS_DOMAIN = [-1, 5];

interface Props {
  irrTh: number;
  score: number;
  data: GetCompanyInferenceResponse['result']['data']['supporting_graphs']['investment_score'];
  className?: string;
}

const Component: React.FC<Props> = ({ irrTh, score, data, className }) => {
  const xAxisDomain = useMemo<ScatterChartProps['xAxisDomain']>(() => {
    return [data.quartiles[0].bottom_th, data.quartiles[data.quartiles.length - 1].top_th];
  }, [data.quartiles]);

  const activeQuartile = useMemo(() => {
    return data.quartiles.find(quartile => quartile.active);
  }, [data.quartiles]);

  const areas = useMemo<ScatterChartProps['areas']>(() => {
    if (activeQuartile) {
      return [
        {
          x1: activeQuartile.bottom_th,
          y1: Y_AXIS_DOMAIN[0],
          x2: activeQuartile.top_th,
          y2: Y_AXIS_DOMAIN[1],
          type: 'success',
        },
      ];
    }

    return [];
  }, [activeQuartile]);

  const columnLabels = useMemo<ScatterChartProps['columnLabels']>(() => {
    return data.quartiles.map(quartile => ({
      x1: quartile.bottom_th,
      y1: Y_AXIS_DOMAIN[0],
      x2: quartile.top_th,
      y2: Y_AXIS_DOMAIN[1],
      labels: [
        { position: 'top', text: `Predsion-${quartile.prec * 100}%` },
        { position: 'topSubtitle', text: `TP-${quartile.tp}` },
        { position: 'bottom', text: `FP=${quartile.fp}` },
      ],
    }));
  }, [data.quartiles]);

  const scatterChartData = useMemo<ScatterChartProps['data']>(() => {
    return data.data.map(data => ({ x: data.proba, y: data.irr }));
  }, [data.data]);

  const lines = useMemo<ScatterChartProps['lines']>(() => {
    return { x: score, y: irrTh };
  }, [irrTh, score]);

  return (
    <div className={clsx(styles.container, className)}>
      <Typography color="var(--text-3-color)" mb="27px" variant="subtitle1">
        ADA Prediction vs Dataset
      </Typography>
      <ScatterChart
        areas={areas}
        columnLabels={columnLabels}
        data={scatterChartData}
        lines={lines}
        xAxisDomain={xAxisDomain}
        xAxisLabel="probas"
        xAxisTickCount={6}
        yAxisDomain={Y_AXIS_DOMAIN}
        yAxisLabel="irr"
        yAxisTickCount={7}
      />
    </div>
  );
};

export const AdaPrediction = memo(Component);
